import { User } from '@/models/Users'
import { useUpdateUserMutation } from '@/utils/hooks/users'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  TextField,
  DialogActions,
  Button,
  Alert,
} from '@mui/material'
import { useState } from 'react'

interface ProfileCompletionModalProps {
  isOpen: boolean
  onComplete: () => void
  token: string | null
  userData: User
}

export default function CompleteUserProfileModal({
  isOpen,
  onComplete,
  token,
  userData,
}: ProfileCompletionModalProps) {
  const [firstName, setFirstName] = useState(userData.first_name || '')
  const [lastName, setLastName] = useState(userData.last_name || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateUserMutation = useUpdateUserMutation()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return

    setIsSubmitting(true)
    try {
      // Call your API to update user profile
      await updateUserMutation.mutateAsync({
        editedUser: {
          ...userData,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
        token,
      })
      onComplete()
    } catch {
      setError('Sorry, something went wrong! Please contact support.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} disableEscapeKeyDown>
      <DialogTitle
        sx={{ textAlign: 'center', color: '#1C3045', fontSize: '24px' }}
      >
        We don't know your name!
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          color="#1C3045"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          We need your name to finish setting up your account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#757575',
              },
            }}
          />

          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#757575',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          disableRipple
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !firstName.trim() || !lastName.trim()}
          sx={{
            borderRadius: 2,
            bgcolor: '#1C3045',
            color: 'white',
            px: 4,
            fontSize: '20px',
            textTransform: 'none',
            boxShadow: 'none',
            mx: 'auto',
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
